import * as React from 'react';
import { t } from '@lingui/core/macro';
import { isSessionOrganizer, userSession } from 'state/session';
import { tenantConfig } from 'utils/tenant';
import { showConfirmDialog } from 'state/confirm-dialog';
import { useWebComponent } from 'components/WebComponentRoot';

export interface ToggleProps {
  className?: string;
}

export const ExperienceToggle: React.FC<ToggleProps> = ({ className = '' }) => {
  const session = userSession().useState();
  // is3DBlocked also depends on tenant flags — subscribe so flag changes re-render (AVS-4270)
  tenantConfig().useState();
  const activeExperience = session.activeExperience || '2D';
  const { webComponent } = useWebComponent();

  const handleToggle = () => {
    void (async () => {
      const newExperience = activeExperience === '3D' ? '2D' : '3D';

      // AVS-4335: switching to 2D while connected to a LocalAVE renderer disconnects it
      if (newExperience === '2D' && userSession().isConnectedToLocalAve) {
        // AVS-4347: claim dialog in the same shadow root that triggered it (e3d-stream's fullscreen overlay)
        const confirmed = await showConfirmDialog({
          dialogId: 'DIALOG_LOCAL_AVE_2D_SWITCH',
          title: t`Switch to 2D?`,
          message: t`Switching to 2D will disconnect the Local AVE session. Do you want to continue?`,
          confirmText: t`Switch to 2D`,
          cancelText: t`Cancel`,
          'data-testid': 'local-ave-2d-switch-dialog',
          preferredClaimant: webComponent?.tagName?.toLowerCase(),
        });

        if (!confirmed) {
          return;
        }
      }

      userSession().setValues({ activeExperience: newExperience });
    })();
  };

  if (!tenantConfig().isAllowed('2D')) {
    return null;
  }

  // Only the session organizer can switch experiences (AVS-4141); outdated cars are 2D-only (AVS-4270)
  const disabled =
    session.comparisonMode === 'on' || (session.isShared && !isSessionOrganizer()) || userSession().is3DBlocked;

  return (
    <div className={`inline-flex items-center ${className}`}>
      {/* Toggle Switch */}
      {/* For local AVE 3D sessions, we color the toggle differently (orange instead of green) */}
      <button
        onClick={handleToggle}
        className={`
          relative inline-flex h-8 w-16 items-center justify-between rounded-full px-2 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
          ${activeExperience === '3D' ? (userSession().isConnectedToLocalAve ? 'bg-orange-900' : 'bg-green-700') : 'bg-gray-300'}
        `}
        aria-pressed={activeExperience === '3D'}
        aria-label={`Switch to ${activeExperience === '3D' ? '2D' : '3D'} experience`}
        data-testid="experience-toggle"
        disabled={disabled}
      >
        {/* Left Label (2D) */}
        <span
          className={`pl-0.5 text-xs font-medium transition-colors duration-200 ${
            activeExperience === '3D' ? 'text-white' : 'text-gray-900'
          }`}
        >
          3D
        </span>

        {/* Toggle Circle */}
        <span
          className={`
            absolute inline-block size-6 rounded-full bg-white shadow-lg transition-transform duration-200 ease-in-out
            ${activeExperience === '3D' ? 'translate-x-6' : 'translate-x-0'}
          `}
        />

        {/* Right Label (3D) */}
        <span
          className={`pr-0.5 text-xs font-medium transition-colors duration-200 ${
            disabled ? 'text-gray-500' : activeExperience === '2D' ? 'text-gray-900' : 'text-white'
          }`}
        >
          2D
        </span>
      </button>
    </div>
  );
};
